import { createStore } from "zustand/vanilla";
import { persist } from "zustand/middleware";

// Types
export interface SelectedOption {
  groupId: string;
  groupName: string;
  optionValue: string;
  optionName: string;
  priceModifier: number;
}

export interface CartItem {
  cartItemId: string;
  productId: string;
  name: string;
  basePrice: number;
  price: number; // basePrice + sum of priceModifiers
  quantity: number;
  image?: string;
  selectedOptions: SelectedOption[];
}

export interface CartState {
  items: CartItem[];
  isOpen: boolean;
}

export interface CartActions {
  addItem: (item: Omit<CartItem, "quantity">, quantity?: number) => void;
  removeItem: (cartItemId: string) => void;
  updateQuantity: (cartItemId: string, quantity: number) => void;
  clearCart: () => void;
  toggleCart: () => void;
  openCart: () => void;
  closeCart: () => void;
}

export type CartStore = CartState & CartActions;

// Default state
export const defaultInitState: CartState = {
  items: [],
  isOpen: false,
};

/**
 * Generates a stable cartItemId from productId + selected options.
 * Same product + same options = same cartItemId (increments quantity).
 * Same product + different options = different cartItemId (separate line).
 */
export function generateCartItemId(
  productId: string,
  selectedOptions: SelectedOption[],
): string {
  const optionKey = [...selectedOptions]
    .sort((a, b) => a.groupId.localeCompare(b.groupId))
    .map((o) => `${o.groupId}:${o.optionValue}`)
    .join("|");
  return optionKey ? `${productId}_${optionKey}` : productId;
}

/**
 * Cart store factory - creates new store instance per provider
 * Uses persist middleware with skipHydration for Next.js SSR compatibility
 * @see https://zustand.docs.pmnd.rs/guides/nextjs#hydration-and-asynchronous-storages
 */
export const createCartStore = (initState: CartState = defaultInitState) => {
  return createStore<CartStore>()(
    persist(
      (set) => ({
        ...initState,

        addItem: (item, quantity = 1) =>
          set((state) => {
            const existing = state.items.find(
              (i) => i.cartItemId === item.cartItemId,
            );
            if (existing) {
              return {
                items: state.items.map((i) =>
                  i.cartItemId === item.cartItemId
                    ? { ...i, quantity: i.quantity + quantity }
                    : i,
                ),
              };
            }
            return { items: [...state.items, { ...item, quantity }] };
          }),

        removeItem: (cartItemId) =>
          set((state) => ({
            items: state.items.filter((i) => i.cartItemId !== cartItemId),
          })),

        updateQuantity: (cartItemId, quantity) =>
          set((state) => {
            if (quantity <= 0) {
              return {
                items: state.items.filter((i) => i.cartItemId !== cartItemId),
              };
            }
            return {
              items: state.items.map((i) =>
                i.cartItemId === cartItemId ? { ...i, quantity } : i,
              ),
            };
          }),

        clearCart: () => set({ items: [] }),
        toggleCart: () => set((state) => ({ isOpen: !state.isOpen })),
        openCart: () => set({ isOpen: true }),
        closeCart: () => set({ isOpen: false }),
      }),
      {
        name: "cart-storage",
        skipHydration: true,
        partialize: (state) => ({ items: state.items }),
      },
    ),
  );
};
